import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Collapse, 
  IconButton, 
  useTheme,
  Chip,
  GlobalStyles,
  Button,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import EnhancedChartGroup from './EnhancedChartGroup';
import { getColorByLabel } from '../utils/colorMapping';

const ChartCardWithFilters = ({ 
  title,
  chartData,
  filterComponent,
  xAxisTitle,
  yAxisTitle,
  isExpanded = false,
  onMinimize,
  cardColor,
  showDatasetToggle = false,
  onDatasetChange,
  chartType = 'bar', // Default to bar charts
  sectionType = null,
  tripTypeOptions = [],
  showFilterIcon = false,
  onFilterIconClick
}) => {
  const theme = useTheme();
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  
  // Get datasets for toggle buttons
  const datasets = chartData?.multiBarData?.datasets || [];
  const [selectedDatasets, setSelectedDatasets] = useState([]);

  // Update selectedDatasets whenever datasets change to include all new datasets
  useEffect(() => {
    setSelectedDatasets(datasets.map((_, index) => index));
  }, [datasets]);

  const toggleFilters = () => {
    setFiltersExpanded(!filtersExpanded);
  };

  // Handle dataset selection toggle
  const handleDatasetToggle = (event, newSelection) => {
    // Prevent deselecting all datasets
    if (newSelection.length === 0) return;
    
    setSelectedDatasets(newSelection);
    
    if (onDatasetChange) {
      // Convert selected indexes back to dataset objects
      const selectedData = newSelection.map(index => datasets[index]);
      onDatasetChange(selectedData);
    }
  };

  return (
    <>
      <GlobalStyles 
        styles={{
          '@keyframes shimmer': {
            '0%': { backgroundPosition: '-200% 0' },
            '100%': { backgroundPosition: '200% 0' }
          }
        }}
      />
      <Card sx={{ 
        mb: 4, 
        boxShadow: isExpanded ? '0 12px 48px rgba(0,0,0,0.15)' : '0 8px 32px rgba(0,0,0,0.08)', 
        borderRadius: 4,
        overflow: 'hidden',
        border: isExpanded 
          ? `2px solid ${cardColor || theme.palette.primary.main}` 
          : `1px solid ${theme.palette.grey[100]}`,
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        background: isExpanded 
          ? `linear-gradient(145deg, #ffffff 0%, ${cardColor || theme.palette.primary.main}05 100%)`
          : 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
        transform: isExpanded ? 'scale(1.01)' : 'scale(1)',
        '&:hover': {
          boxShadow: isExpanded ? '0 20px 80px rgba(0,0,0,0.2)' : '0 16px 64px rgba(0,0,0,0.12)',
          transform: isExpanded ? 'scale(1.01) translateY(-2px)' : 'translateY(-4px)',
          borderColor: cardColor || theme.palette.primary.light
        }
      }}>
      <CardContent sx={{ p: 0 }}>
        {/* Expanded Card Header with Minimize Button */}
        {isExpanded && onMinimize && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            background: `linear-gradient(135deg, ${cardColor || theme.palette.primary.main}15 0%, ${cardColor || theme.palette.primary.main}05 100%)`,
            borderBottom: `2px solid ${cardColor || theme.palette.primary.main}30`
          }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700,
                color: cardColor || theme.palette.primary.main,
                fontSize: '1.3rem',
                letterSpacing: '0.5px',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              {title}
              {showFilterIcon && (
                <IconButton
                  onClick={onFilterIconClick}
                  size="small"
                  sx={{
                    color: cardColor || theme.palette.primary.main,
                    opacity: 0.7,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      opacity: 1,
                      transform: 'scale(1.1)',
                      backgroundColor: `${cardColor || theme.palette.primary.main}15`
                    }
                  }}
                >
                  <FilterListIcon fontSize="small" />
                </IconButton>
              )}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Dataset Toggle Buttons - Show if we have any datasets and card is expanded */}
              {datasets.length >= 1 && (
                <ToggleButtonGroup
                  value={selectedDatasets}
                  onChange={handleDatasetToggle}
                  size="small"
                  color="primary"
                  aria-label="dataset toggle"
                  sx={{
                    '& .MuiToggleButton-root': {
                      borderRadius: 2,
                      mx: 0.25,
                      transition: 'all 0.2s ease-in-out',
                      border: 'none',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                      }
                    }
                  }}
                >
                  {datasets.map((dataset, index) => (
                    <ToggleButton 
                      key={index}
                      value={index}
                      aria-label={dataset.label}
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        borderColor: 'transparent',
                        minWidth: 'auto',
                        color: '#ffffff',
                        '&.Mui-selected': {
                          backgroundColor: `${getColorByLabel(dataset.label, 'border', chartType)} !important`,
                          color: '#ffffff !important',
                          fontWeight: 700,
                          border: 'none !important',
                          '&:hover': {
                            backgroundColor: `${getColorByLabel(dataset.label, 'border', chartType)} !important`,
                            opacity: 0.9,
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                          }
                        },
                        '&:not(.Mui-selected)': {
                          backgroundColor: `${getColorByLabel(dataset.label, 'border', chartType)}40`,
                          color: `${getColorByLabel(dataset.label, 'border', chartType)} !important`,
                          border: `1px solid ${getColorByLabel(dataset.label, 'border', chartType)}60 !important`,
                          fontWeight: 600,
                          '&:hover': {
                            backgroundColor: `${getColorByLabel(dataset.label, 'border', chartType)}60`,
                            color: '#ffffff !important',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                          }
                        }
                      }}
                    >
                      {dataset.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              )}
              
              <Button
                startIcon={<CloseIcon />}
                onClick={onMinimize}
                variant="outlined"
                size="small"
                sx={{
                  borderColor: cardColor || theme.palette.primary.main,
                  color: cardColor || theme.palette.primary.main,
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 36,
                  '&:hover': {
                    backgroundColor: cardColor ? cardColor + '10' : theme.palette.primary.main + '10',
                    borderColor: cardColor || theme.palette.primary.main,
                    transform: 'scale(1.05)'
                  }
                }}
              >
                Minimize
              </Button>
            </Box>
          </Box>
        )}
        
        {/* Filter Section Header - Only show if filterComponent exists */}
        {filterComponent && (
          <>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                p: 3,
                background: `linear-gradient(135deg, ${theme.palette.grey[25]} 0%, ${theme.palette.grey[50]} 50%, ${theme.palette.grey[75]} 100%)`,
                borderBottom: filtersExpanded 
                  ? `2px solid ${theme.palette.grey[300]}` 
                  : `1px solid ${theme.palette.grey[150]}`,
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${theme.palette.grey[100]} 50%, ${theme.palette.grey[125]} 100%)`,
                  borderBottomColor: theme.palette.grey[400]
                }
              }}
              onClick={toggleFilters}
            >
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                p: 1.5,
                borderRadius: 3,
                backgroundColor: (cardColor || theme.palette.primary.main) + '12',
                border: `2px solid ${(cardColor || theme.palette.primary.main)}20`,
                mr: 2.5,
                transition: 'all 0.3s ease',
                boxShadow: `0 4px 12px ${(cardColor || theme.palette.primary.main)}15`
              }}>
                <FilterListIcon 
                  sx={{ 
                    color: cardColor || theme.palette.primary.main,
                    fontSize: 22,
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                  }}
                />
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700,
                    color: cardColor || theme.palette.primary.main,
                    mb: 0.5,
                    fontSize: '1.1rem',
                    letterSpacing: '0.5px'
                  }}
                >
                  {title} Filters
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    fontStyle: 'italic',
                    fontSize: '0.85rem',
                    opacity: 0.8
                  }}
                >
                </Typography>
              </Box>

              <IconButton 
                size="medium"
                sx={{ 
                  color: cardColor || theme.palette.primary.main,
                  transform: filtersExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  backgroundColor: (cardColor || theme.palette.primary.main) + '08',
                  border: `2px solid ${(cardColor || theme.palette.primary.main)}20`,
                  boxShadow: `0 4px 12px ${(cardColor || theme.palette.primary.main)}15`,
                  '&:hover': {
                    backgroundColor: (cardColor || theme.palette.primary.main) + '15',
                    transform: filtersExpanded ? 'rotate(180deg) scale(1.1)' : 'rotate(0deg) scale(1.1)',
                    boxShadow: `0 6px 20px ${(cardColor || theme.palette.primary.main)}25`
                  }
                }}
              >
                <ExpandMoreIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </Box>
            
            {/* Collapsible Filter Content */}
            <Collapse in={filtersExpanded} timeout={500}>
              <Box sx={{ 
                p: 4, 
                backgroundColor: `linear-gradient(145deg, ${theme.palette.grey[25]} 0%, white 100%)`,
                borderBottom: `1px solid ${theme.palette.grey[200]}`,
                position: 'relative',
                '& > *': {
                  border: 'none !important',
                  boxShadow: 'none !important'
                }
              }}>
                {filterComponent}
              </Box>
            </Collapse>
          </>
        )}
        
        {/* Chart Content */}
        <Box sx={{ 
          p: filterComponent ? 4 : 5,
          backgroundColor: 'white',
          background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
          minHeight: isExpanded ? 500 : 400
        }}>
          <EnhancedChartGroup
            title={!isExpanded ? title : ''} // Don't show title in chart if card is expanded (already in header)
            barData={chartData?.barData}
            multiBarData={chartData?.multiBarData}
            chartData={chartData}
            xAxisTitle={xAxisTitle || "Time Period"}
            yAxisTitle={yAxisTitle || "Count"}
            isExpanded={isExpanded}
            visibleDatasets={datasets.length >= 1 ? selectedDatasets.map(index => datasets[index]) : null}
            sectionType={sectionType}
            tripTypeOptions={tripTypeOptions}
          />
        </Box>
      </CardContent>
    </Card>
    </>
  );
};

export default ChartCardWithFilters;
